"use client";
import { motion } from "framer-motion";
import { Check, Eye, EyeOff, Lock, Mail, User, X } from "lucide-react";
import Link from "next/link";
import { redirect } from "next/navigation";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { useActionState, useEffect, useState } from "react";
import { registerAction, registerTeamMemberAction } from "@/actions/auth";
import EkomLogo from "@/components/logo";
import { FeatureCards } from "@/components/sign-up/feature-cards";
import { TrustedCompanies } from "@/components/sign-up/trusted-companies";
import { SimpleThemeToggle } from "@/components/theme-switcher";
import { BackgroundGradientAnimation } from "@/components/ui/background-gradient-animation";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { parseUIMode } from "@/lib/utils";
import type { UIMode } from "@/types/user";

export default function SignUpForm({
  isJoiningTeam = false,
  defaultValues,
}: {
  isJoiningTeam?: boolean;
  defaultValues?: {
    fullName: string;
    email: string;
    password: string;
    confirmPassword: string;
    termsAccepted: boolean;
    token?: string;
  };
}) {
  const t = useTranslations();
  const { setTheme } = useTheme();
  const [state, formAction, isLoading] = useActionState(
    isJoiningTeam ? registerTeamMemberAction : registerAction,
    null,
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState(
    defaultValues
      ? defaultValues
      : {
          fullName: "",
          email: "",
          password: "",
          confirmPassword: "",
          termsAccepted: false,
          token: "",
        },
  );

  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSymbol: false,
    passwordsMatch: false,
  });

  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);

  useEffect(() => {
    if (state?.error) {
      toast({
        title: t("notifications.error"),
        description: state.error,
        variant: "destructive",
      });
    } else if (state?.success) {
      setTheme(parseUIMode(state.user?.ui_mode as UIMode));
      redirect("/app/validating/account/connections");
    }
  }, [state, t, setTheme]);

  useEffect(() => {
    const { password, confirmPassword } = formData;

    setPasswordValidation({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSymbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      passwordsMatch:
        password === confirmPassword && confirmPassword.length > 0,
    });
  }, [formData]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    return (
      formData.fullName.trim().length > 0 &&
      formData.email.trim().length > 0 &&
      passwordValidation.minLength &&
      passwordValidation.hasUpperCase &&
      passwordValidation.hasNumber &&
      passwordValidation.hasSymbol &&
      passwordValidation.passwordsMatch &&
      formData.termsAccepted
    );
  };

  // Extract condition for cleaner JSX
  const isReadOnlyMode = isJoiningTeam && Boolean(defaultValues?.email);
  const emailInputProps = isReadOnlyMode
    ? { defaultValue: formData.email, readOnly: true }
    : {
        value: formData.email,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          handleInputChange("email", e.target.value),
      };
  const tokenInputProps = isReadOnlyMode
    ? { defaultValue: formData.token, readOnly: true }
    : { value: formData.token, readOnly: true };

  const inputClassName =
    "pl-10 md:pl-11 h-11 bg-input/30 border border-border focus-visible:!border-primary focus-visible:!ring-primary/20";

  return (
    <BackgroundGradientAnimation>
      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-20">
        <SimpleThemeToggle />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.4 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: 0.4,
          ease: "easeOut",
        }}
      >
        <div className="relative z-10 flex min-h-screen items-center justify-center px-6 md:px-8 py-8">
          <div className="w-full max-w-[calc(100%-2rem)] md:max-w-3xl lg:max-w-6xl max-h-[90vh] rounded-xl lg:rounded-3xl overflow-hidden flex flex-col lg:flex-row lg:shadow-2xl shadow-xl">
            <div className="hidden lg:flex lg:w-2/5 bg-primary p-12 flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <EkomLogo size={32} color="white" />
                  <span className="text-2xl font-bold text-white">EKOM</span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">
                  {t("signUp.heroTitle")}
                </h2>
                <p className="text-white/80 text-sm mb-8">
                  {t("signUp.heroSubtitle")}
                </p>
                <FeatureCards />
              </div>
              <TrustedCompanies />
            </div>

            <div className="flex-1 flex items-center justify-center p-6 md:p-8 lg:w-1/2 bg-card/80 overflow-y-auto">
              <div className="w-full max-w-sm md:max-w-md">
                <div className="mb-8 md:mb-10">
                  <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-3 text-left">
                    {isJoiningTeam
                      ? t("signUp.joinYourTeam")
                      : t("signUp.title")}
                  </h1>
                  <p className="text-muted-foreground text-sm md:text-base text-left">
                    {t("signUp.subtitle")}
                  </p>
                </div>

                <form className="space-y-4 md:space-y-5" action={formAction}>
                  <div className="space-y-2">
                    <label
                      htmlFor="fullName"
                      className="text-sm font-medium text-foreground"
                    >
                      {t("signUp.fullName")}
                    </label>
                    <div className="relative focus-within:[&>svg]:text-primary transition-colors">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 size-4 md:size-5 text-muted-foreground" />
                      <Input
                        id="fullName"
                        type="text"
                        placeholder={t("signUp.fullNamePlaceholder")}
                        className={inputClassName}
                        name="fullName"
                        value={formData.fullName}
                        onChange={(e) =>
                          handleInputChange("fullName", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="text-sm font-medium text-foreground"
                    >
                      {t("signUp.emailAddress")}
                    </label>
                    <div className="relative focus-within:[&>svg]:text-primary transition-colors">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 md:size-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder={t("signUp.emailPlaceholder")}
                        className={inputClassName}
                        name="email"
                        {...emailInputProps}
                        required
                      />
                      <Input
                        id="token"
                        type="text"
                        placeholder={t("signUp.emailPlaceholder")}
                        className="hidden"
                        name="token"
                        {...tokenInputProps}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="password"
                      className="text-sm font-medium text-foreground"
                    >
                      {t("signUp.password")}
                    </label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={t("signUp.passwordPlaceholder")}
                        className={inputClassName}
                        name="password"
                        value={formData.password}
                        onChange={(e) =>
                          handleInputChange("password", e.target.value)
                        }
                        onFocus={() => setShowPasswordRequirements(true)}
                        required
                      />
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 md:size-5 text-muted-foreground transition-colors z-10 pointer-events-none peer-focus:text-primary" />

                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="size-4 md:size-5" />
                        ) : (
                          <Eye className="size-4 md:size-5" />
                        )}
                      </button>
                    </div>

                    {showPasswordRequirements &&
                      formData.password.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {!passwordValidation.minLength && (
                            <div className="flex items-center gap-2 text-xs text-destructive">
                              <X className="w-3 h-3" />
                              <span>{t("signUp.validation.minLength")}</span>
                            </div>
                          )}
                          {passwordValidation.minLength && (
                            <div className="flex gap-2">
                              <ValidationItem
                                isValid={passwordValidation.hasUpperCase}
                                text={t("signUp.validation.hasUpperCase")}
                              />
                              <ValidationItem
                                isValid={passwordValidation.hasNumber}
                                text={t("signUp.validation.hasNumber")}
                              />
                              <ValidationItem
                                isValid={passwordValidation.hasSymbol}
                                text={t("signUp.validation.hasSymbol")}
                              />
                            </div>
                          )}
                        </div>
                      )}
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="confirmPassword"
                      className="text-sm font-medium text-foreground"
                    >
                      {t("signUp.confirmPassword")}
                    </label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder={t("signUp.confirmPasswordPlaceholder")}
                        className={inputClassName}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={(e) =>
                          handleInputChange("confirmPassword", e.target.value)
                        }
                        required
                      />
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 md:size-5 text-muted-foreground transition-colors z-10 pointer-events-none peer-focus:text-primary" />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="size-4 md:size-5" />
                        ) : (
                          <Eye className="size-4 md:size-5" />
                        )}
                      </button>
                    </div>
                    {formData.confirmPassword.length > 0 && (
                      <ValidationItem
                        isValid={passwordValidation.passwordsMatch}
                        text={t("signUp.validation.passwordsMatch")}
                      />
                    )}
                  </div>

                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="terms"
                      checked={formData.termsAccepted}
                      onCheckedChange={(checked) =>
                        handleInputChange("termsAccepted", checked as boolean)
                      }
                      className="border-input bg-input/30 cursor-pointer"
                      required
                    />
                    <label
                      htmlFor="terms"
                      className="text-sm text-muted-foreground cursor-pointer leading-tight"
                    >
                      {t("signUp.agreeToTerms")}{" "}
                      <Link
                        href="https://www.ekom.ai/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline font-medium"
                      >
                        {t("signUp.termsOfService")}
                      </Link>{" "}
                      {t("signUp.and")}{" "}
                      <Link
                        href="https://www.ekom.ai/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline font-medium"
                      >
                        {t("signUp.privacyPolicy")}
                      </Link>
                    </label>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading || !isFormValid()}
                    className="w-full h-11 md:h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm md:text-base mt-6 md:mt-5"
                  >
                    {isLoading
                      ? t("signUp.creatingAccount")
                      : isJoiningTeam
                        ? t("signUp.joinYourTeam")
                        : t("signUp.createAccount")}
                  </Button>
                </form>

                <div className="mt-6 md:mt-8 text-center">
                  <span className="text-sm text-muted-foreground">
                    {t("signUp.haveAccount")}{" "}
                  </span>
                  <Link
                    href="/login"
                    className="text-sm text-primary hover:underline font-semibold"
                  >
                    {t("signUp.signIn")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </BackgroundGradientAnimation>
  );
}

function ValidationItem({ isValid, text }: { isValid: boolean; text: string }) {
  return (
    <div
      className={`flex items-center gap-2 text-xs ${isValid ? "text-success" : "text-destructive"}`}
    >
      {isValid ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
      <span>{text}</span>
    </div>
  );
}
